import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';

import { MailSettings } from './MailSettings';
import { PlatformMfaSettings } from './PlatformMfaSettings';
import { AiInsightsSettings } from './AiInsightsSettings';
import { SsoSettings } from './SsoSettings';
import { LegalDocuments } from './LegalDocuments';

/**
 * Configuration — the platform-wide superadmin settings, consolidated from
 * three separate pages (Mail settings, MFA policy, AI / Insights) into one
 * tabbed surface. Each panel is the original page component rendered in
 * `embedded` mode (its own h4/description suppressed) so this page owns the
 * header. The active tab lives in the URL
 * (`?tab=mail|mfa|insights|sso|legal`) so the old routes can deep-link straight
 * to the right tab.
 *
 * Enterprise SSO is the one tab that isn't platform-scoped — a connection
 * belongs to a tenant — so it carries its own tenant picker and keeps the
 * choice in the URL (`?tab=sso&tenant=<id>`). It lives here because
 * Configuration is where admins look for the SSO switches; the same panel
 * is also on the Tenant Detail page, in the context of one tenant.
 */
const TABS = [
  { key: 'mail', label: 'Mail', render: () => <MailSettings embedded /> },
  {
    key: 'mfa',
    label: 'MFA policy',
    render: () => <PlatformMfaSettings embedded />,
  },
  {
    key: 'insights',
    label: 'AI / Insights',
    render: () => <AiInsightsSettings embedded />,
  },
  {
    key: 'sso',
    label: 'Enterprise SSO',
    render: () => <SsoSettings embedded />,
  },
  {
    key: 'legal',
    label: 'Terms & privacy',
    render: () => <LegalDocuments embedded />,
  },
] as const;

export function Configuration() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const active = Math.max(
    0,
    TABS.findIndex((t) => t.key === requested),
  );

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h4">Configuration</Typography>
        <Typography variant="body2" color="text.secondary">
          Platform-wide mail, security and AI settings, each tenant's
          enterprise SSO connection, and the Terms of Use and Privacy Policy.
        </Typography>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={active}
          onChange={(_e, v: number) =>
            setParams({ tab: TABS[v].key }, { replace: true })
          }
          aria-label="Configuration sections"
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <Box>{TABS[active].render()}</Box>
    </Stack>
  );
}
